import { customHeaders, requestApi } from 'api/api'
import { v4 as uuidv4 } from 'uuid'
import {
  AddCustomerWalletRequestV2,
  Application,
  AssignCustomerFeeWalletPayload,
  AssignCustomerFeeWalletResponse,
  BusinessCustomerResponse,
  CreateBusinessCustomer,
  CreateCustomerAgreement,
  CustomerAgreementCreation,
  CustomerApplicationDetails,
  CustomersRequest,
  CustomerWalletProfile,
  CustomerWalletProfileV2Response,
  CustomerWalletsRequest,
  CustomerWalletsV2Response,
  CustomerWalletV2Individual,
  GetCustomerFeeWalletsResponse,
  GetCustomerWalletProfilesRequest,
  Industries,
  MonthlyVolume,
  WalletResponse,
} from 'types/customers'
import { WithPaginationApiResponse } from 'types/pagination'
import { CountryState } from 'types/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

export const CUSTOMER_API_URL = '/platform/v1/customers'
export const CUSTOMER_V2_API_URL = '/platform/v2/customers'

const LUCENE_SPECIAL_CHARS = /["\\]/g

const escapeSearchQueryTerm = (term: string): string =>
  term.replaceAll(LUCENE_SPECIAL_CHARS, char => `\\${char}`)

export const getCustomers = <CustomerSummary extends object>({
  search,
  size,
  page,
  sort,
  filters,
}: CustomersRequest): Promise<WithPaginationApiResponse<CustomerSummary[]>> => {
  return requestApi({
    url: CUSTOMER_API_URL,
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(search && { name: search }),
      ...(filters && { ...filters }),
    },
  })
}

export const postAddCustomer = <CustomerType extends object>(
  customer: CustomerType
): Promise<void> => {
  return requestApi({
    url: CUSTOMER_API_URL,
    method: 'POST',
    data: customer,
  })
}

export const getCustomer = <CustomerDetails extends object>(
  customerReference: string
): Promise<CustomerDetails> => {
  return requestApi({
    url: `${CUSTOMER_API_URL}/${customerReference}`,
    method: 'GET',
  })
}

export const patchCustomer = <CustomerPatchData extends object>({
  customerReference,
  data,
}: {
  customerReference: string
  data: CustomerPatchData
}): Promise<void> => {
  return requestApi({
    url: `${CUSTOMER_API_URL}/${customerReference}`,
    method: 'PATCH',
    data,
  })
}

export const getAllMonthlyVolumes = (): Promise<MonthlyVolume[]> => {
  return requestApi({
    url: '/platform/v1/accounts/monthly-expected-volumes',
    method: 'GET',
  })
}

export const getIndustries = (): Promise<Industries[]> => {
  return requestApi({
    url: '/platform/v1/accounts/industries',
    method: 'GET',
  })
}

export const getCountryStates = (countryCode: string): Promise<CountryState[]> => {
  return requestApi({
    url: `/platform/v1/accounts/country/${countryCode}/states`,
    method: 'GET',
    baseUrl: process.env.API_URL,
  })
}

export const postAddCustomerWalletV2 = (
  wallet: AddCustomerWalletRequestV2
): Promise<WalletResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v2/wallets',
    method: 'POST',
    data: wallet,
    headers: {
      ...customHeaders,
      'Idempotency-Key': uuidv4(),
    } as any,
  })
}

export const getCustomerWallets = ({
  customerReference,
  currencyCodeSearch,
  currencyCodes,
  currencyType,
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  statuses,
}: CustomerWalletsRequest): Promise<CustomerWalletsV2Response> => {
  const currencyQuery = currencyCodes?.length
    ? `currency:(${currencyCodes.join(' OR ')})`
    : currencyCodeSearch && `currency:${currencyCodeSearch}`
  const searchQuery = [
    customerReference && `customerId:${customerReference}`,
    currencyType && `currencyType:${currencyType}`,
    currencyQuery,
    searchTerm && `name:"${escapeSearchQueryTerm(searchTerm)}"`,
    statuses?.length && `status:(${statuses.join(' OR ')})`,
  ]
    .filter(Boolean)
    .join(' AND ')

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v2/wallets',
    method: 'GET',
    params: {
      ...(searchQuery && { q: searchQuery }),
      pageSize: size,
      pageNumber: page,
      ...(sort && { sort }),
    },
  })
}

export const getCustomerWallet = (walletId: string): Promise<CustomerWalletV2Individual> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/ledger/v2/wallets/${walletId}`,
    method: 'GET',
  })
}

export const getWalletProfilesV2 = async (
  request?: GetCustomerWalletProfilesRequest
): Promise<CustomerWalletProfile> => {
  const { currencyCodes, paymentMethods, customerId } = request || {}

  const queryParts: string[] = []

  if (customerId) {
    queryParts.push(`customerId:${customerId}`)
  }

  if (currencyCodes?.length) {
    const currencyQuery =
      currencyCodes.length === 1
        ? `currency:${currencyCodes[0].toUpperCase()}`
        : `currency:(${currencyCodes.map(currency => currency.toUpperCase()).join(' OR ')})`
    queryParts.push(currencyQuery)
  }

  if (paymentMethods?.length) {
    const methodQuery =
      paymentMethods.length === 1
        ? `method:${paymentMethods[0].toLowerCase()}`
        : `method:(${paymentMethods.map(method => method.toLowerCase()).join(' OR ')})`
    queryParts.push(methodQuery)
  }

  const searchQuery = queryParts?.length > 0 ? queryParts.join(' AND ') : undefined

  const response: CustomerWalletProfileV2Response = await requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v2/wallets/profiles',
    method: 'GET',
    params: {
      ...(searchQuery && { q: searchQuery }),
    },
  })

  return {
    profiles: response?.content,
  }
}

export const assignCustomerFeeWallet = (
  payload: AssignCustomerFeeWalletPayload
): Promise<AssignCustomerFeeWalletResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/fees/customer-fee-wallets',
    method: 'PUT',
    data: payload,
  })
}

export const getCustomerFeeWallets = (): Promise<GetCustomerFeeWalletsResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/fees/customer-fee-wallets',
    method: 'GET',
  })
}

export interface GetApplicationsParams {
  page?: number
  size?: number
  name?: string
  statuses?: string[]
  types?: string[]
  useCases?: string[]
}

export const getApplications = ({
  page = API_PAGINATION_OFFSET,
  size = API_PAGINATION_LIMIT,
  name,
  statuses,
  types,
  useCases,
}: GetApplicationsParams = {}): Promise<WithPaginationApiResponse<Application[]>> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: CUSTOMER_V2_API_URL,
    method: 'GET',
    params: {
      pageNumber: page,
      pageSize: size,
      ...(name ? { name } : {}),
      ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
      ...(types?.length ? { types: types.join(',') } : {}),
      ...(useCases?.length ? { useCases: useCases.join(',') } : {}),
    },
  })
}

export const postAddBusinessCustomer = (
  customer: CreateBusinessCustomer
): Promise<BusinessCustomerResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: CUSTOMER_V2_API_URL,
    method: 'POST',
    data: customer,
  })
}

export const getCustomerApplication = <
  CustomerApplicationDetailsType extends object = CustomerApplicationDetails,
>(
  onboardingId: string
): Promise<CustomerApplicationDetailsType> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `${CUSTOMER_V2_API_URL}/${onboardingId}`,
    method: 'GET',
  })
}

export const postCustomerAgreementCreation = (
  agreementCustomer: CreateCustomerAgreement
): Promise<CustomerAgreementCreation> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v2/agreements',
    method: 'POST',
    data: agreementCustomer,
    headers: {
      'X-Onboarding-Initial-Flow': 'true',
    },
  })
}
